import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Link2, Loader2, Newspaper, Music2, Film, Facebook, Instagram, Youtube } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { api, apiError } from "@/lib/api";

const STEPS = ["Lecture de la page…", "Écriture du reportage…", "Lancement du montage 60 s…"];

function Hint({ icon: Icon, title, text }) {
  return (
    <div className="flex gap-3 rounded-lg bg-white border border-[#dddfe2] p-4" data-testid={`link-hint-${title.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <Icon className="w-5 h-5 text-[#1877f2] shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs text-[#65676b]">{text}</p>
      </div>
    </div>
  );
}

export default function LinkVideoPage() {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const nav = useNavigate();

  const go = async (e) => {
    e.preventDefault();
    setError("");
    if (url.trim().length < 8) return setError("Collez le lien complet d'un article ou d'une vidéo TikTok, Facebook, Instagram ou YouTube");
    setBusy(true);
    setStep(0);
    const timer = setInterval(() => setStep(s => Math.min(s + 1, STEPS.length - 1)), 9000);
    try {
      await api.videoFromLink(url.trim());
      toast.success("Vidéo lancée ! Elle arrive dans votre galerie dans 1 à 3 min.");
      nav("/app");
    } catch (err) {
      setError(apiError(err));
    } finally {
      clearInterval(timer);
      setBusy(false);
    }
  };

  return (
    <>
      <AppNav current="link" />
      <main className="max-w-[860px] mx-auto px-4 lg:px-10 py-8 space-y-6" data-testid="link-video-page">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Créer une vidéo depuis un lien</h1>
          <p className="text-sm text-[#65676b] mt-2">Collez un article d'actualité ou une vidéo TikTok, Facebook (reel ou live), Instagram ou YouTube : BuzzlyMe en fait un reportage de 60 s avec voix off, sous-titres et outro.</p>
        </div>
        <form onSubmit={go} className="card-surface p-6 space-y-3" data-testid="link-video-form">
          <label htmlFor="link-url" className="text-sm font-semibold">Lien de la page</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8d91]" />
              <input id="link-url" value={url} onChange={e => setUrl(e.target.value)} disabled={busy} data-testid="link-url-input"
                placeholder="Lien d'article, TikTok, Facebook, Instagram ou YouTube"
                className="w-full rounded-lg border border-[#ccd0d5] bg-white pl-9 pr-3 py-3 text-sm outline-none focus:border-[#1877f2]" />
            </div>
            <button type="submit" className="btn-primary justify-center !py-3" disabled={busy} data-testid="link-generate-btn">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {STEPS[step]}</> : <><Film className="w-4 h-4" /> Créer la vidéo</>}
            </button>
          </div>
          {error && <p className="text-sm text-red-600 rounded-lg bg-red-50 px-4 py-3" data-testid="link-error">{error}</p>}
          <p className="text-xs text-[#8a8d91]">Si le lien ne peut pas être lu, rien n'est décompté de votre quota de vidéos.</p>
        </form>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Hint icon={Newspaper} title="Article d'actualité" text="Titre, texte et image principale sont lus gratuitement sur la page." />
          <Hint icon={Music2} title="Vidéo TikTok" text="Vrais extraits de la vidéo, avec son texte, ses vues et ses likes." />
          <Hint icon={Facebook} title="Vidéo Facebook" text="Vrais extraits d'une vidéo, d'un reel ou d'un long live public, lus gratuitement." />
          <Hint icon={Instagram} title="Reel Instagram" text="Vrais extraits d'un reel public, avec sa légende, ses vues et ses likes." />
          <Hint icon={Youtube} title="Vidéo YouTube" text="Titre et description lus gratuitement ; extraits vidéo si YouTube les laisse lire, sinon images de la vidéo." />
        </div>
      </main>
    </>
  );
}
